import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  DeleteOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  GlobalOutlined,
  CodeOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  SyncOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { servicesService, ordersService } from '../../services';
import { ServiceEvent, ServiceEventResponse } from '../../services/services.service';
import { ServiceStatusBadge, ConfirmationModal, ServiceEventsList, DetailPageHeader, SecurityRulesTab, CustomDomainsTab, MachineSpecsCard, ServiceLogo, HeaderActionButton, HeaderActionMenu } from '../../components/shared';
import { getServiceAge } from '../../utils/serviceHelpers';
import { useNotificationContext } from '../../contexts/NotificationContext';
import MonitoringTab from '../ServiceDetail/components/MonitoringTab';
import LazyServiceEventsList from '../../components/shared/LazyServiceEventsList';

// ─── Helpers ────────────────────────────────────────────
const mapLevelToEventType = (level?: string): 'info' | 'warning' | 'error' | 'success' => {
  if (!level) return 'info';
  const l = level.toUpperCase();
  if (l === 'SUCCESS') return 'success';
  if (l === 'ERROR') return 'error';
  if (l === 'WARNING') return 'warning';
  return 'info';
};

const mapStackStatusToEventType = (s?: string): 'info' | 'warning' | 'error' | 'success' => {
  if (!s) return 'info';
  const u = s.toUpperCase();
  if (u.includes('COMPLETE') && !u.includes('FAILED')) return 'success';
  if (u.includes('FAILED') || u.includes('ROLLBACK')) return 'error';
  return 'info';
};

const mapServiceEventsToUI = (events: ServiceEventResponse[]): ServiceEvent[] =>
  events.map((e, i) => {
    let timestamp: Date;
    try { timestamp = new Date(e.createdAt); } catch { timestamp = new Date(); }
    const eventType = e.level ? mapLevelToEventType(e.level) : mapStackStatusToEventType(e.stackStatus);
    const message = e.message || 'No message';
    const isKubiyEvent = e.source?.toLowerCase() === 'kubiy';
    const msgLower = message.toLowerCase();
    const isDeletionEvent = msgLower.includes('deleted') || msgLower.includes('eliminado');

    return {
      id: e.PK || e.SK || `event-${i}`,
      timestamp,
      type: eventType,
      message,
      details: e.description || e.statusReason || undefined,
      source: e.source || 'System',
      isKubiyEvent,
      isDeletionEvent,
    };
  });

const isCreatingStatus = (s?: string): boolean => {
  if (!s) return false;
  const n = s.toLowerCase();
  return ['deploying', 'initializing', 'stacking', 'creating'].includes(n);
};

// ─── Componente Principal ───────────────────────────────
const ToolJetDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error: showError } = useNotificationContext();

  const [service, setService] = useState<any>(null);
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<ServiceEvent[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'connection' | 'events' | 'monitoring' | 'security' | 'domains'>('overview');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Credentials (DB only — admin is via signup wizard)
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [dbCredentials, setDbCredentials] = useState<{ username?: string; password?: string } | null>(null);
  const [loadingCreds, setLoadingCreds] = useState(false);
  const [dbPasswordVisible, setDbPasswordVisible] = useState(false);

  const hasFetchedRef = useRef<string | null>(null);

  // ─── Fetch Data ───────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      if (!id) { setError('No service ID provided'); setLoading(false); return; }
      if (hasFetchedRef.current === id) return;
      hasFetchedRef.current = id;

      try {
        setLoading(true);
        setError(null);

        const svc: any = await servicesService.getById(id);

        const createdAt = svc.createdAt ? (typeof svc.createdAt === 'string' ? new Date(svc.createdAt) : svc.createdAt) : new Date();
        const updatedAt = svc.updatedAt ? (typeof svc.updatedAt === 'string' ? new Date(svc.updatedAt) : svc.updatedAt) : new Date();

        setService({ ...svc, status: svc.status?.toLowerCase(), createdAt, updatedAt });

        if (svc.orderId) {
          try {
            const orderData = await ordersService.getById(svc.orderId);
            setOrder(orderData);
          } catch { /* ignore */ }
        }

      } catch (err: any) {
        setError(err.message || 'Error loading service');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // ─── Polling while creating ───────────────────────────
  useEffect(() => {
    if (!id || !service || !isCreatingStatus(service.status)) return;

    const interval = setInterval(async () => {
      try {
        const svc: any = await servicesService.getById(id);
        const status = svc.status?.toLowerCase();
        const createdAt = svc.createdAt ? (typeof svc.createdAt === 'string' ? new Date(svc.createdAt) : svc.createdAt) : new Date();
        const updatedAt = svc.updatedAt ? (typeof svc.updatedAt === 'string' ? new Date(svc.updatedAt) : svc.updatedAt) : new Date();
        setService({ ...svc, status, createdAt, updatedAt });

        if (!isCreatingStatus(status)) {
          clearInterval(interval);
          const eventsData = await servicesService.getEvents(id);
          setEvents(mapServiceEventsToUI(eventsData));
        }
      } catch { /* ignore */ }
    }, 5000);

    return () => clearInterval(interval);
  }, [id, service?.status]);

  // ─── Refresh ──────────────────────────────────────────
  const handleRefresh = async () => {
    if (!id) return;
    try {
      setIsRefreshing(true);
      const svc: any = await servicesService.getById(id);
      const createdAt = svc.createdAt ? (typeof svc.createdAt === 'string' ? new Date(svc.createdAt) : svc.createdAt) : new Date();
      const updatedAt = svc.updatedAt ? (typeof svc.updatedAt === 'string' ? new Date(svc.updatedAt) : svc.updatedAt) : new Date();
      setService({ ...svc, status: svc.status?.toLowerCase(), createdAt, updatedAt });

      const eventsData = await servicesService.getEvents(id);
      setEvents(mapServiceEventsToUI(eventsData));
      success(t('tooljet.detail.refreshed') || 'Service updated');
    } catch (err: any) {
      showError(err.message || 'Error refreshing');
    } finally {
      setIsRefreshing(false);
    }
  };

  // ─── Delete ───────────────────────────────────────────
  const handleDelete = async () => {
    if (!id) return;
    try {
      setIsDeleting(true);
      await servicesService.delete(id);
      success(t('tooljet.detail.deleted') || 'Service deleted');
      navigate('/tooljet');
    } catch (err: any) {
      showError(err.message || 'Error deleting service');
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // ─── Copy helper ──────────────────────────────────────
  const copyText = (text: string, label?: string) => {
    navigator.clipboard.writeText(text);
    success(`${label || 'Value'} copied`);
  };

  // ─── Credentials (DB only) ───────────────────────────
  const handleOpenCredentialsModal = () => {
    setDbCredentials(null);
    setDbPasswordVisible(false);
    setCredentialsModalOpen(true);
  };

  const handleFetchDbCredentials = async () => {
    if (!id) return;
    setLoadingCreds(true);
    try {
      const dbData = await servicesService.getTooljetDbCredentials(id);
      setDbCredentials({ username: dbData.username, password: dbData.password });
      setDbPasswordVisible(false);
    } catch (err: any) {
      showError(err?.message || 'Error fetching database credentials');
    } finally {
      setLoadingCreds(false);
    }
  };

  // ─── VM Control (Start / Stop / Restart / Connect) ───
  const handleConnect = () => {
    if (!service || !id) return;
    navigate(`/tooljet/terminal/${id}`);
  };

  const handleStart = async () => {
    if (!service || !id) return;
    setIsProcessing(true);
    try {
      const response = await servicesService.start(id);
      if (response) {
        const finalService = await servicesService.monitorServiceStatus(
          id,
          ['running', 'stopped', 'deleted'],
          (updatedService) => {
            const svc = updatedService as any;
            const status = (svc.status || 'starting')?.toLowerCase();
            setService((prev: any) => ({ ...prev, ...svc, status }));
          }
        );
        const svc = finalService as any;
        const createdAt = svc.createdAt ? (typeof svc.createdAt === 'string' ? new Date(svc.createdAt) : svc.createdAt) : new Date();
        const updatedAt = svc.updatedAt ? (typeof svc.updatedAt === 'string' ? new Date(svc.updatedAt) : svc.updatedAt) : new Date();
        setService({ ...svc, status: svc.status?.toLowerCase(), createdAt, updatedAt });

        try {
          const eventsData = await servicesService.getEvents(id);
          setEvents(mapServiceEventsToUI(eventsData));
        } catch { /* ignore */ }

        success(t('notifications.vm.startSuccess') || 'Service started');
      }
    } catch (err: any) {
      showError(err?.message || t('notifications.vm.startError') || 'Error starting service');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStop = async () => {
    if (!service || !id) return;
    setIsProcessing(true);
    try {
      const response = await servicesService.stop(id);
      if (response) {
        const finalService = await servicesService.monitorServiceStatus(
          id,
          ['stopped', 'running', 'deleted'],
          (updatedService) => {
            const svc = updatedService as any;
            const status = (svc.status || 'stopping')?.toLowerCase();
            setService((prev: any) => ({ ...prev, ...svc, status }));
          }
        );
        const svc = finalService as any;
        const createdAt = svc.createdAt ? (typeof svc.createdAt === 'string' ? new Date(svc.createdAt) : svc.createdAt) : new Date();
        const updatedAt = svc.updatedAt ? (typeof svc.updatedAt === 'string' ? new Date(svc.updatedAt) : svc.updatedAt) : new Date();
        setService({ ...svc, status: svc.status?.toLowerCase(), createdAt, updatedAt });

        try {
          const eventsData = await servicesService.getEvents(id);
          setEvents(mapServiceEventsToUI(eventsData));
        } catch { /* ignore */ }

        success(t('notifications.vm.stopSuccess') || 'Service stopped');
      }
    } catch (err: any) {
      showError(err?.message || t('notifications.vm.stopError') || 'Error stopping service');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRestart = async () => {
    if (!service || !id) return;
    setIsProcessing(true);
    try {
      const response = await servicesService.restart(id);
      if (response) {
        const finalService = await servicesService.monitorServiceStatus(
          id,
          ['running', 'stopped', 'deleted'],
          (updatedService) => {
            const svc = updatedService as any;
            const status = (svc.status || 'restarting')?.toLowerCase();
            setService((prev: any) => ({ ...prev, ...svc, status }));
          }
        );
        const svc = finalService as any;
        const createdAt = svc.createdAt ? (typeof svc.createdAt === 'string' ? new Date(svc.createdAt) : svc.createdAt) : new Date();
        const updatedAt = svc.updatedAt ? (typeof svc.updatedAt === 'string' ? new Date(svc.updatedAt) : svc.updatedAt) : new Date();
        setService({ ...svc, status: svc.status?.toLowerCase(), createdAt, updatedAt });

        try {
          const eventsData = await servicesService.getEvents(id);
          setEvents(mapServiceEventsToUI(eventsData));
        } catch { /* ignore */ }

        success(t('notifications.vm.restartSuccess') || 'Service restarted');
      }
    } catch (err: any) {
      showError(err?.message || t('notifications.vm.restartError') || 'Error restarting service');
    } finally {
      setIsProcessing(false);
    }
  };

  // ─── Loading / Error ──────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingOutlined className="text-4xl text-sky-600" spin />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="max-w-[1400px] mx-auto px-8 py-6">
        <button onClick={() => navigate('/tooljet')} className="mb-4 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
          <ArrowLeftOutlined /> {t('tooljet.detail.back') || 'Back'}
        </button>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <p className="text-red-800 dark:text-red-200">{error || 'Service not found'}</p>
        </div>
      </div>
    );
  }

  // ─── Derived Data ─────────────────────────────────────
  const tooljetConfig = service.metadata?.tooljetConfig || {};
  const instanceType = tooljetConfig.instanceType || service.metadata?.instanceType || '-';
  const storage = tooljetConfig.storage || service.metadata?.volumeGB || '-';
  const region = service.region || tooljetConfig.region || '-';
  const publicIp = service.publicIp || service.metadata?.publicIp;
  const tooljetUrl = service.endpointUrlAdmin || null;
  const tooljetHost = service.publicDnsNameKubiy || publicIp;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
      <DetailPageHeader
        backLabel={t('tooljet.detail.back') || 'Back to ToolJet Services'}
        onBack={() => navigate('/tooljet')}
        title={service.name}
        subtitle={`ToolJet • ${instanceType} • ${region}`}
        icon={<ServiceLogo category="TOOLJET" />}
        actions={
          <>
            {service.status === 'running' && (
              <HeaderActionButton variant="primary" icon={<CodeOutlined />} onClick={handleConnect}>
                {t('virtualMachines.detail.connect') || 'Connect'}
              </HeaderActionButton>
            )}
            {service.status === 'stopped' && (
              <HeaderActionButton variant="success" icon={<PlayCircleOutlined />} onClick={handleStart} disabled={isProcessing}>
                {t('virtualMachines.detail.start') || 'Start'}
              </HeaderActionButton>
            )}
            {service.status === 'running' && (
              <>
                <HeaderActionButton variant="secondary" icon={<PauseCircleOutlined />} onClick={handleStop} disabled={isProcessing}>
                  {t('virtualMachines.detail.stop') || 'Stop'}
                </HeaderActionButton>
                <HeaderActionButton variant="secondary" icon={<SyncOutlined />} onClick={handleRestart} disabled={isProcessing}>
                  {t('virtualMachines.detail.restart') || 'Restart'}
                </HeaderActionButton>
              </>
            )}
            <HeaderActionMenu
              items={[
                { key: 'refresh', label: t('tooljet.detail.refresh') || 'Refresh', icon: <ReloadOutlined />, onClick: () => handleRefresh(), disabled: isRefreshing },
                { key: 'credentials', label: t('tooljet.detail.dbCredentials') || 'DB Credentials', icon: <KeyOutlined />, onClick: handleOpenCredentialsModal },
                { key: 'delete', label: t('tooljet.detail.delete') || 'Delete', icon: <DeleteOutlined />, onClick: () => setShowDeleteModal(true), danger: true, disabled: isProcessing },
              ]}
            />
          </>
        }
      />

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-8 space-y-6">
          {/* Overview Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('tooljet.detail.overview') || 'Overview'}</h2>
              <ServiceStatusBadge status={service.status} size="md" showLabel />
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Service ID</label>
                <div className="flex items-center gap-2">
                  <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white">{service.id}</code>
                  <button onClick={() => copyText(service.id, 'Service ID')} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    <CopyOutlined />
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('tooljet.fields.instanceType') || 'Instance Type'}</label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{instanceType}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('tooljet.fields.storage') || 'Storage'}</label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{storage} GB</p>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('tooljet.fields.region') || 'Region'}</label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{region}</p>
              </div>
              {publicIp && (
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Public IP</label>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white">{publicIp}</code>
                    <button onClick={() => copyText(publicIp, 'IP')} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                      <CopyOutlined />
                    </button>
                  </div>
                </div>
              )}
              {tooljetUrl && (
                <div className="sm:col-span-2">
                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">ToolJet URL</label>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded font-mono text-gray-900 dark:text-white flex-1 truncate">{tooljetUrl}</code>
                    <button onClick={() => copyText(tooljetUrl, 'ToolJet URL')} className="p-1 text-gray-400 hover:text-gray-600"><CopyOutlined /></button>
                    <a href={tooljetUrl} target="_blank" rel="noopener noreferrer" className="px-2 py-1 text-xs font-medium text-white bg-sky-600 hover:bg-sky-700 rounded transition-colors">Open</a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="border-b border-gray-200 dark:border-gray-700 flex gap-1 px-6 overflow-x-auto">
              {(['overview', 'connection', 'events', 'monitoring', 'security', 'domains'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                    activeTab === tab
                      ? 'border-black dark:border-white text-gray-900 dark:text-white'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {t(`tooljet.tabs.${tab}`) || tab}
                </button>
              ))}
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  <MachineSpecsCard service={service} />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('tooljet.detail.serviceConfig') || 'Service Configuration'}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">ToolJet App</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Port 3000</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">PostgreSQL</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Local (port 5432)</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Redis</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Local (port 6379)</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">PostgREST</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Local (ToolJet DB)</p>
                    </div>
                  </div>

                  {/* Signup wizard info */}
                  <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-start gap-3">
                      <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                          {t('tooljet.detail.signupTip') || 'Admin Account Setup'}
                        </p>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                          {t('tooljet.detail.signupTipDesc') || 'The first time you access ToolJet, you will be prompted to create your admin account. Use the email you configured and create your password.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Quick links when running */}
                  {service.status === 'running' && tooljetUrl && (
                    <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-start gap-3">
                        <CheckCircleOutlined className="text-green-600 dark:text-green-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-800 dark:text-green-200">
                            {t('tooljet.detail.running') || 'ToolJet is running and ready to use.'}
                          </p>
                          <div className="mt-3 space-y-2">
                            <a
                              href={tooljetUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-sm text-green-700 dark:text-green-300 hover:underline"
                            >
                              <GlobalOutlined /> Open ToolJet
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Connection Tab */}
              {activeTab === 'connection' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('tooljet.detail.connectionInfo') || 'Connection Information'}
                  </h3>

                  {!tooljetHost ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        {t('tooljet.detail.noIpYet') || 'Connection info not available yet. Wait for the service to finish provisioning.'}
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* ToolJet Connection */}
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">ToolJet Connection</h4>
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs text-gray-500 dark:text-gray-400">Host</label>
                            <div className="flex items-center gap-2 mt-0.5">
                              <code className="text-sm bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded font-mono text-gray-900 dark:text-white flex-1">{tooljetHost}</code>
                              <button onClick={() => copyText(tooljetHost!, 'Host')} className="p-1.5 text-gray-400 hover:text-gray-600"><CopyOutlined /></button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-gray-500 dark:text-gray-400">Port</label>
                              <p className="text-sm font-mono text-gray-900 dark:text-white mt-0.5">3000</p>
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 dark:text-gray-400">DB Credentials</label>
                              <button
                                onClick={handleOpenCredentialsModal}
                                className="text-sm text-blue-600 dark:text-blue-400 hover:underline mt-0.5 flex items-center gap-1"
                              >
                                <KeyOutlined className="text-xs" /> View DB credentials
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Ports */}
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Open Ports</h4>
                        <div className="space-y-2">
                          {[
                            { port: '3000', protocol: 'HTTP', desc: 'ToolJet' },
                            { port: '5432', protocol: 'PostgreSQL', desc: 'Internal only' },
                            { port: '6379', protocol: 'Redis', desc: 'Internal only' },
                          ].map((p) => (
                            <div key={p.port} className="flex items-center justify-between py-1.5">
                              <div className="flex items-center gap-3">
                                <code className="text-sm font-mono font-semibold text-gray-900 dark:text-white w-14">{p.port}</code>
                                <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-400">{p.protocol}</span>
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400">{p.desc}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Events Tab */}
              {activeTab === 'events' && (
                <LazyServiceEventsList serviceId={id || ''} eventsPerPage={10} />
              )}

              {activeTab === 'monitoring' && (
                <MonitoringTab service={service} />
              )}
              {activeTab === 'security' && (
                <SecurityRulesTab
                  serviceId={id || ''}
                  securityRules={service.securityRules}
                  securityGroupId={service.securityGroupId}
                />
              )}
              {activeTab === 'domains' && (
                <CustomDomainsTab serviceId={id || ''} fqdn={service.fqdn} />
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-4 space-y-6">
          {/* Status Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('tooljet.detail.status') || 'Status'}</h3>
            </div>
            <div className="p-6 space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
                <ServiceStatusBadge status={service.status} />
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Created</span>
                <span className="text-sm text-gray-900 dark:text-white">{getServiceAge(service.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Updated</span>
                <span className="text-sm text-gray-900 dark:text-white">{getServiceAge(service.updatedAt)}</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                {t('tooljet.detail.quickActions') || 'Quick Actions'}
              </h3>
            </div>
            <div className="p-6 space-y-3">
              {service.status === 'running' && (
                <>
                  <button
                    onClick={handleConnect}
                    className="w-full px-4 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                  >
                    <CodeOutlined />
                    {t('virtualMachines.detail.connect') || 'Connect Terminal'}
                  </button>
                  {tooljetUrl && (
                    <a
                      href={tooljetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full px-4 py-2.5 bg-sky-600 text-white rounded-lg font-medium hover:bg-sky-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <GlobalOutlined />
                      Open ToolJet
                    </a>
                  )}
                  <button
                    onClick={handleStop}
                    disabled={isProcessing}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <PauseCircleOutlined />
                    {t('virtualMachines.detail.stop') || 'Stop'}
                  </button>
                  <button
                    onClick={handleRestart}
                    disabled={isProcessing}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <SyncOutlined />
                    {t('virtualMachines.detail.restart') || 'Restart'}
                  </button>
                </>
              )}
              {service.status === 'stopped' && (
                <button
                  onClick={handleStart}
                  disabled={isProcessing}
                  className="w-full px-4 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <PlayCircleOutlined />
                  {t('virtualMachines.detail.start') || 'Start'}
                </button>
              )}
              {isProcessing && (
                <div className="flex items-center justify-center gap-2 text-sm text-blue-600 dark:text-blue-400 py-2">
                  <LoadingOutlined spin />
                  <span>{t('tooljet.detail.processing') || 'Processing...'}</span>
                </div>
              )}
              <button
                onClick={handleOpenCredentialsModal}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
              >
                <KeyOutlined />
                {t('tooljet.detail.dbCredentials') || 'DB Credentials'}
              </button>
            </div>
          </div>

          {/* Order Info */}
          {order && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Order</h3>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Order ID</span>
                  <button onClick={() => navigate(`/orders/${order.id}`)} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                    {order.id.substring(0, 16)}...
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Documentación */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Documentación</h3>
            <a
              href="https://docs.kubiy.com/marketplace/tooljet"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <span>Ver documentación</span>
              <GlobalOutlined className="text-xs" />
            </a>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={t('tooljet.detail.deleteTitle') || 'Delete ToolJet Service'}
        message={t('tooljet.detail.deleteMessage', { name: service.name }) || `Are you sure you want to delete "${service.name}"?`}
        confirmText={t('tooljet.detail.delete') || 'Delete'}
        cancelText={t('tooljet.detail.cancel') || 'Cancel'}
        variant="danger"
        loading={isDeleting}
      />

      {/* DB Credentials Modal */}
      {credentialsModalOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setCredentialsModalOpen(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('tooljet.detail.dbCredentialsTitle') || 'Database Credentials'}</h2>

              {!dbCredentials ? (
                <div className="py-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {t('tooljet.detail.dbCredentialsDesc') || 'Click below to retrieve your PostgreSQL database credentials.'}
                  </p>
                  <button
                    onClick={handleFetchDbCredentials}
                    disabled={loadingCreds}
                    className="flex items-center gap-2 px-4 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50"
                  >
                    {loadingCreds ? <LoadingOutlined spin /> : <EyeOutlined />}
                    {t('tooljet.detail.recoverCredentials') || 'Recover Credentials'}
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Database Credentials Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <KeyOutlined className="text-blue-600" />
                      {t('tooljet.detail.dbCredentialsSection') || 'PostgreSQL Credentials'}
                    </h3>
                    <div className="space-y-3 pl-1">
                      <div>
                        <label className="text-sm text-gray-500 mb-1 block">Username</label>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-sm bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded font-mono">{dbCredentials.username || '—'}</code>
                          <button
                            onClick={() => dbCredentials.username && copyText(dbCredentials.username, 'Username')}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                          >
                            <CopyOutlined />
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500 mb-1 block">Password</label>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-sm bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded font-mono break-all">
                            {dbPasswordVisible ? dbCredentials.password : '••••••••'}
                          </code>
                          <button
                            onClick={() => dbCredentials.password && copyText(dbCredentials.password, 'Password')}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                          >
                            <CopyOutlined />
                          </button>
                          <button
                            onClick={() => setDbPasswordVisible(!dbPasswordVisible)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                          >
                            {dbPasswordVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs text-gray-500">Host</label>
                          <p className="text-sm font-mono text-gray-900 dark:text-white">127.0.0.1</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Port</label>
                          <p className="text-sm font-mono text-gray-900 dark:text-white">5432</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Database</label>
                          <p className="text-sm font-mono text-gray-900 dark:text-white">tooljet_db</p>
                        </div>
                      </div>
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
                        <p className="text-xs text-blue-700 dark:text-blue-300 flex items-center gap-1">
                          <InfoCircleOutlined /> {t('tooljet.detail.dbNote') || 'PostgreSQL, Redis, and PostgREST run locally on the same instance'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => { setCredentialsModalOpen(false); setDbCredentials(null); setDbPasswordVisible(false); }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ToolJetDetail;
